// FILE: src/services/questionAttemptsService.js
//
// Supabase persistence + aggregation queries for question_attempts (Phase 1
// analytics foundation introduced in migration 011).
//
// Responsibilities
//   1. buildAttemptRows()      Pure function — converts session + per-question
//                              state into one row per question ready for a
//                              batch insert.  Exported so tests can verify the
//                              shape without mocking Supabase.
//   2. persistQuestionAttempts() Fire-and-forget batch insert called from
//                              sessionService.persistExamSession() after the
//                              parent exam_sessions row has been written.
//   3. loadQuestionPerformance() Reads the question_performance_summary view
//                              for the Admin → Question Performance screen.
//
// Error handling matches sessionService — errors are logged but never thrown
// so analytics failures can never break the learner's results page.

import { supabase } from './supabaseClient';

// ── Row builder ─────────────────────────────────────────────────────────────

/**
 * Builds one question_attempts row per question in the session.
 *
 * @param {object} params
 * @param {object} params.session         - session descriptor (examId, questions[])
 * @param {string} params.sessionId       - uuid returned by the parent exam_sessions insert
 * @param {object} params.questionStates  - { [index]: { selectedAnswers, submitted, timeSpentSeconds } }
 * @param {Set}    params.skippedIndices  - indices still in the skip queue at session end
 * @param {Set}    [params.flaggedIndices] - indices the learner flagged; defaults to empty
 * @param {string} [params.source]         - 'user' | 'test'; defaults to 'user'
 * @param {Date|string} [params.answeredAt] - timestamp used for answered_at/created_at defaults
 * @param {string|null} [params.userId]    - signed-in learner's auth.users.id; null for anonymous
 * @returns {object[]} rows ready for supabase.from('question_attempts').insert(rows)
 */
export function buildAttemptRows({
  session,
  sessionId,
  questionStates,
  skippedIndices,
  flaggedIndices = new Set(),
  source = 'user',
  answeredAt = null,
  userId = null,
}) {
  if (!session || !Array.isArray(session.questions)) return [];
  if (!sessionId) return [];

  const answeredAtIso = answeredAt
    ? new Date(answeredAt).toISOString()
    : new Date().toISOString();

  return session.questions.map((q, index) => {
    const state = questionStates?.[index] || {};
    const submitted = state.submitted === true;
    const selected  = Array.isArray(state.selectedAnswers) ? state.selectedAnswers : [];
    const wasSkipped = !submitted && skippedIndices?.has?.(index) === true;
    const wasFlagged = flaggedIndices?.has?.(index) === true;

    let isCorrect = null;
    if (submitted) {
      const correct = q.correctAnswers || [];
      isCorrect =
        selected.length === correct.length &&
        correct.every((id) => selected.includes(id));
    }

    const timeSpentSeconds =
      typeof state.timeSpentSeconds === 'number' && state.timeSpentSeconds >= 0
        ? Math.round(state.timeSpentSeconds)
        : null;

    // Intelligent Pace Coaching §5: how many times the learner changed a
    // previously-selected answer on this question. Defaults to 0.
    const answerChanges =
      typeof state.answerChanges === 'number' && state.answerChanges >= 0
        ? Math.round(state.answerChanges)
        : 0;

    return {
      session_id:         sessionId,
      question_id:        String(q.id),
      exam_id:            session.examId,
      question_order:     index,
      is_correct:         isCorrect,
      was_answered:       submitted,
      was_skipped:        wasSkipped,
      was_flagged:        wasFlagged,
      time_spent_seconds: timeSpentSeconds,
      answer_changes:     answerChanges,
      selected_answers:   selected.length > 0 ? selected : null,
      // Phase 1 always writes false.  Phase 2 will pass through q.isExperimental.
      is_experimental:    q.isExperimental === true,
      // Case Studies inside sims (migration 076): NULL for independent
      // questions and for standalone case practice.
      case_study_id:       q.caseId ?? null,
      case_stage_sequence: q.caseStageSequence ?? null,
      source,
      answered_at:        submitted ? answeredAtIso : null,
      // Phase A: mirror exam_sessions.user_id. Null for anonymous learners.
      user_id:            userId || null,
    };
  });
}

// ── Persistence ─────────────────────────────────────────────────────────────

/**
 * Batch-inserts one question_attempts row per question for the given session.
 * Fire-and-forget — errors are logged but never thrown.
 *
 * @param {object} params - same shape as buildAttemptRows()
 * @returns {Promise<{ inserted: number, error: string | null }>}
 */
export async function persistQuestionAttempts(params) {
  try {
    const rows = buildAttemptRows(params);
    if (rows.length === 0) {
      return { inserted: 0, error: null };
    }

    const { error } = await supabase.from('question_attempts').insert(rows);
    if (error) {
      console.warn('[questionAttemptsService] Insert failed:', error.message);
      return { inserted: 0, error: error.message };
    }

    console.info(
      '[questionAttemptsService] Attempts persisted:',
      params.session?.examId,
      `${rows.length} row${rows.length === 1 ? '' : 's'}`,
    );
    return { inserted: rows.length, error: null };
  } catch (err) {
    console.warn('[questionAttemptsService] Unexpected error:', err);
    return { inserted: 0, error: err?.message || String(err) };
  }
}

// ── Aggregation query ──────────────────────────────────────────────────────

/**
 * Loads per-question performance metrics from the question_performance_summary
 * view.  Filtered by examId when provided; otherwise returns every exam.
 *
 * The view already filters to source='user', so CI / Playwright runs are
 * excluded by construction.
 *
 * @param {string} [examId] - optional 'pmp' | 'cpmai' filter
 * @returns {Promise<PerformanceMetric[]>}
 *
 * PerformanceMetric shape:
 *   {
 *     questionId:       string,
 *     examId:           string,
 *     attempts:         number,
 *     correctCount:     number,
 *     answeredCount:    number,
 *     skippedCount:     number,
 *     flaggedCount:     number,
 *     experimentalCount:number,
 *     avgTimeSeconds:   number | null,
 *     lastAttemptedAt:  string | null,      // ISO 8601
 *     correctRate:      number,             // 0.0 – 1.0 over attempts
 *     skipRate:         number,             // 0.0 – 1.0 over attempts
 *     flagRate:         number,             // 0.0 – 1.0 over attempts
 *   }
 */
export async function loadQuestionPerformance(examId = null) {
  try {
    let query = supabase
      .from('question_performance_summary')
      .select('*');

    if (examId) query = query.eq('exam_id', examId);

    const { data, error } = await query;
    if (error) {
      console.warn('[questionAttemptsService] View read failed:', error.message);
      return [];
    }
    if (!Array.isArray(data)) return [];

    return data.map(normalizePerformanceRow);
  } catch (err) {
    console.warn('[questionAttemptsService] Unexpected error reading view:', err);
    return [];
  }
}

// ── Normalization ───────────────────────────────────────────────────────────

/**
 * Converts a raw question_performance_summary row (snake_case, bigint COUNT
 * results come back as strings from PostgREST) into the camelCase shape the
 * UI consumes.
 *
 * Exported for unit tests.
 */
export function normalizePerformanceRow(row) {
  const attempts         = toInt(row.attempts);
  const correctCount     = toInt(row.correct_count);
  const answeredCount    = toInt(row.answered_count);
  const skippedCount     = toInt(row.skipped_count);
  const flaggedCount     = toInt(row.flagged_count);
  const experimentalCount = toInt(row.experimental_count);

  const safeDiv = (n, d) => (d > 0 ? n / d : 0);

  return {
    questionId:        row.question_id,
    examId:            row.exam_id,
    attempts,
    correctCount,
    answeredCount,
    skippedCount,
    flaggedCount,
    experimentalCount,
    avgTimeSeconds:    row.avg_time_seconds == null ? null : toInt(row.avg_time_seconds),
    lastAttemptedAt:   row.last_attempted_at || null,
    correctRate:       safeDiv(correctCount, attempts),
    skipRate:          safeDiv(skippedCount, attempts),
    flagRate:          safeDiv(flaggedCount, attempts),
  };
}

function toInt(value) {
  if (value == null) return 0;
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
}
